import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import Index from "./pages/Index";
import { RootLayout } from "./components/layout/RootLayout";

// Lazy-load non-home routes so the homepage doesn't pay the parse cost
// for every page in the app. Reduces Total Blocking Time on first load.
const OurStory = lazy(() => import("./pages/OurStory"));
const OurServices = lazy(() => import("./pages/OurServices"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const OurTechnology = lazy(() => import("./pages/OurTechnology"));
const Contact = lazy(() => import("./pages/Contact"));
const Industries = lazy(() => import("./pages/Industries"));
const GetProposal = lazy(() => import("./pages/GetProposal"));
const Careers = lazy(() => import("./pages/Careers"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const BookAMeeting = lazy(() => import("./pages/BookAMeeting"));

const queryClient = new QueryClient();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Push virtual pageview to GTM dataLayer on every SPA route change
function GTMPageView() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: "pageview",
      page: {
        path: pathname + search,
        title: document.title,
        location: window.location.href,
      },
    });
  }, [pathname, search]);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <GTMPageView />
        <Suspense fallback={null}>
          <Routes>
            <Route element={<RootLayout />}>
              <Route path="/" element={<Index />} />
              <Route path="/our-story" element={<OurStory />} />
              <Route path="/our-services" element={<OurServices />} />
              <Route path="/our-services/:slug" element={<ServiceDetail />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
              <Route path="/our-technology" element={<OurTechnology />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/vlogs" element={<Blog />} />
              <Route path="/vlogs/:slug" element={<BlogDetail />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/get-a-proposal" element={<GetProposal />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/book-a-meeting" element={<BookAMeeting />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
